import { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonSpinner,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_POST_FOR_EDIT } from '../../../../graphql/queries/postForEdit';
import { GET_CHANNEL_DRAFTS } from '../../../../graphql/queries/channelDrafts';
import { GET_CHANNEL_POSTS } from '../../../../graphql/queries/channelPosts';
import { GET_CHANNEL_TIMELINE } from '../../../../graphql/queries/channelTimeline';
import { SAVE_DRAFT_POST } from '../../../../graphql/mutations/saveDraftPost';
import { PUBLISH_POST } from '../../../../graphql/mutations/publishPost';
import type {
  GetPostForEditQuery,
  GetPostForEditQueryVariables,
  SaveDraftPostMutation,
  SaveDraftPostMutationVariables,
  PublishPostMutation,
  PublishPostMutationVariables,
} from '../../../../types/graphql-generated';
import { useDeletePost } from '../../../../hooks/useDeletePost';
import { AppSectionHeader } from '../../../components/ui/AppSectionHeader';
import { PostComposer } from './PostComposer';
import { DeleteConfirmAlert } from './DeleteConfirmAlert';
import { PostPhotoEditor } from './photos/PostPhotoEditor';
import { usePostPhotoChanges } from './photos/usePostPhotoChanges';

// Full-screen editor to create a new post or edit an existing one. When a
// postId is present it seeds the composer from the loaded post (once) and every
// save targets that post; otherwise the save creates a new post.
//
// Photo changes stay in the editor until "Guardar borrador" / "Publicar", like
// the text, and are sent in the same request, so a failed save never leaves a
// post half edited.
export const CreateEditPost: React.FC = () => {
  const ionRouter = useIonRouter();
  const [presentToast] = useIonToast();
  const { channelId: routeChannelId, postId } = useParams<'channelId' | 'postId'>();
  const channelId = routeChannelId!;
  const isEditing = Boolean(postId);

  const { data, loading: loadingPost } = useQuery<
    GetPostForEditQuery,
    GetPostForEditQueryVariables
  >(GET_POST_FOR_EDIT, {
    variables: { id: postId ?? '' },
    skip: !postId,
  });

  // Resync the owner's lists in the background so they pick up the saved post.
  // Not awaited: goBack() fires as soon as the mutation resolves and the fresh
  // data lands asynchronously once the refetch completes — a rejected refetch
  // must never reject the mutation and falsely report a save/publish failure.
  // Bare DocumentNodes refetch the active observers with their current
  // variables — critical for the paginated feed/timeline whose live variables
  // include limit/offset and must not be clobbered.
  const [saveDraft, { loading: savingDraft }] = useMutation<
    SaveDraftPostMutation,
    SaveDraftPostMutationVariables
  >(SAVE_DRAFT_POST, {
    refetchQueries: [GET_CHANNEL_DRAFTS],
  });
  const [publish, { loading: publishing }] = useMutation<
    PublishPostMutation,
    PublishPostMutationVariables
  >(PUBLISH_POST, {
    refetchQueries: [GET_CHANNEL_DRAFTS, GET_CHANNEL_POSTS, GET_CHANNEL_TIMELINE],
  });
  const { confirmDelete, deleting } = useDeletePost({
    successMessage: 'Borrador eliminado',
    errorMessage: 'No se pudo eliminar el borrador',
  });
  const photoChanges = usePostPhotoChanges();

  const [text, setText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Seed the editor from the loaded post exactly once (guarded by its id), the
  // same render-time pattern EditChannel uses, keeping the field editable after.
  const post = data?.post;
  const [seededId, setSeededId] = useState<string | null>(null);
  if (post && post.id !== seededId) {
    setSeededId(post.id);
    setText(post.text ?? '');
    setLinkUrl(post.linkUrl ?? '');
    setLinkTitle(post.linkTitle ?? '');
    photoChanges.reset(post.photos);
  }

  // Editing an already-published post can only re-publish it: saving it back as
  // a draft (which would unpublish it) is not offered.
  const isPublished = post?.status === 'published';
  // Only drafts are deletable from the editor; published posts are deleted from
  // the post detail screen instead.
  const isDraft = post?.status === 'draft';

  // Always send both link fields: an empty field is sent as null, which clears
  // the link on the backend. The photo fields are only sent when the photos
  // changed: omitting them keeps them.
  const buildInput = () => ({
    channelId,
    postId,
    text,
    linkUrl: linkUrl.trim() || null,
    linkTitle: linkTitle.trim() || null,
    ...photoChanges.toInput(),
  });

  const showToast = (message: string, danger = false) => {
    presentToast({
      message,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      cssClass: danger ? 'toast-danger' : 'toast-success',
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const handleSaveDraft = async () => {
    setErrorMessage(undefined);
    try {
      const result = await saveDraft({
        variables: { input: buildInput() },
      });
      const errors = result.data?.saveDraftPost?.errors ?? [];
      if (errors.length > 0) {
        setErrorMessage(errors[0]);
        return;
      }
      showToast('Borrador guardado');
      ionRouter.goBack();
    } catch {
      showToast('No se pudo guardar el borrador. Inténtalo de nuevo.', true);
    }
  };

  const handlePublish = async () => {
    setErrorMessage(undefined);
    try {
      const result = await publish({
        variables: { input: buildInput() },
      });
      const errors = result.data?.publishPost?.errors ?? [];
      if (errors.length > 0) {
        setErrorMessage(errors[0]);
        return;
      }
      showToast('Publicación publicada');
      ionRouter.goBack();
    } catch {
      showToast('No se pudo publicar. Inténtalo de nuevo.', true);
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/main/channels/${channelId}`} />
          </IonButtons>
          <IonTitle className="ml-4">
            {isEditing ? 'Editar publicación' : 'Nueva publicación'}
          </IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent className="ion-padding">
        {isEditing && loadingPost ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <PostComposer
            value={text}
            onChange={setText}
            linkUrl={linkUrl}
            linkTitle={linkTitle}
            onLinkUrlChange={setLinkUrl}
            onLinkTitleChange={setLinkTitle}
            onSaveDraft={handleSaveDraft}
            onPublish={handlePublish}
            savingDraft={savingDraft}
            publishing={publishing}
            errorMessage={errorMessage}
            allowDraft={!isPublished}
            onDelete={isDraft ? () => setShowDeleteConfirm(true) : undefined}
            deleting={deleting}
            photos={
              <PostPhotoEditor
                photos={photoChanges.photos}
                onAddFromCamera={() => void photoChanges.addFromCamera()}
                onAddFromGallery={() => void photoChanges.addFromGallery()}
                onRemove={photoChanges.remove}
                disabled={savingDraft || publishing || deleting}
              />
            }
          />
        )}

        <DeleteConfirmAlert
          isOpen={showDeleteConfirm}
          header="Eliminar borrador"
          message="¿Seguro que quieres eliminar este borrador? Esta acción no se puede deshacer."
          onDidDismiss={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            if (postId) {
              void confirmDelete({ channelId, postId });
            }
          }}
        />
      </IonContent>
    </IonPage>
  );
};
